import { BellOutlined } from '@ant-design/icons';
import { history, useRequest } from '@umijs/max';
import { Badge, Button, Empty, List, Popover, Typography } from 'antd';
import React from 'react';
import {
  getProjectAccountNotifications,
  getProjectAccountReminderSummary,
  getProjectTaskNotifications,
  readProjectAccountNotification,
  readProjectNotification,
} from '@/services/project/api';
import type {
  ProjectAccountNotification,
  ProjectTaskNotification,
} from '@/services/project/types';
import { unboundMediaBuyerReminderTarget } from './projectAccountReminderNavigation';

const rowsOf = (res: any): ProjectAccountNotification[] =>
  Array.isArray(res)
    ? res
    : Array.isArray(res?.data)
      ? res.data
      : Array.isArray(res?.data?.data)
        ? res.data.data
        : [];

const taskRowsOf = (res: any): ProjectTaskNotification[] =>
  Array.isArray(res)
    ? res
    : Array.isArray(res?.data)
      ? res.data
      : Array.isArray(res?.data?.data)
        ? res.data.data
        : [];

const reminderCountOf = (res: any): number =>
  Math.max(
    0,
    Number(
      res?.unboundMediaBuyerCount ??
        res?.data?.unboundMediaBuyerCount ??
        res?.data?.data?.unboundMediaBuyerCount ??
        0,
    ) || 0,
  );

type NotificationRow =
  | (ProjectAccountNotification & { source: 'account'; sortTime?: string })
  | (ProjectTaskNotification & { source: 'task'; sortTime?: string });

const ProjectAccountNotifications: React.FC = () => {
  const { data: accountData, refresh: refreshAccount } = useRequest(
    getProjectAccountNotifications,
    { pollingInterval: 30000 },
  );
  const { data: taskData, refresh: refreshTask } = useRequest(
    getProjectTaskNotifications,
    { pollingInterval: 30000 },
  );
  const { data: reminderData, refresh: refreshReminder } = useRequest(
    getProjectAccountReminderSummary,
    { pollingInterval: 30000 },
  );
  const rows: NotificationRow[] = [
    ...rowsOf(accountData).map((item) => ({
      ...item,
      source: 'account' as const,
      sortTime: item.createdAt,
    })),
    ...taskRowsOf(taskData).map((item) => ({
      ...item,
      source: 'task' as const,
      sortTime: item.lastNotifiedAt || item.firstNotifiedAt,
    })),
  ].sort((a, b) => (b.sortTime || '').localeCompare(a.sortTime || ''));
  const unboundMediaBuyerCount = reminderCountOf(reminderData);
  const unread =
    rows.filter((item) => !item.readAt).length + unboundMediaBuyerCount;
  const refresh = async () => {
    await Promise.all([refreshAccount(), refreshTask(), refreshReminder()]);
  };

  return (
    <Popover
      placement="bottomRight"
      trigger="click"
      title="@我的消息"
      content={
        <div style={{ width: 380, maxHeight: 460, overflow: 'auto' }}>
          <List.Item
            style={{ cursor: 'pointer' }}
            onClick={() => history.push(unboundMediaBuyerReminderTarget())}
          >
            <List.Item.Meta
              title={
                <Typography.Text strong>
                  投放项目待绑定投手 · {unboundMediaBuyerCount}
                </Typography.Text>
              }
              description="点击查看投放项目表中尚未绑定投手的项目"
            />
          </List.Item>
          {rows.length ? (
            <List
              dataSource={rows}
              renderItem={(item) => (
                <List.Item
                  style={{ cursor: 'pointer', opacity: item.readAt ? 0.65 : 1 }}
                  onClick={async () => {
                    if (!item.readAt) {
                      if (item.source === 'account')
                        await readProjectAccountNotification(item.id);
                      else await readProjectNotification(item.id);
                    }
                    await refresh();
                    history.push(
                      item.source === 'account'
                        ? '/project-table/account-opening'
                        : '/project-table/developer',
                    );
                  }}
                >
                  <List.Item.Meta
                    title={
                      <Typography.Text strong={!item.readAt}>
                        {item.source === 'account'
                          ? '开户备注'
                          : `项目任务${item.projectCode ? ` · ${item.projectCode}` : ''}`}{' '}
                        · {item.title}
                      </Typography.Text>
                    }
                    description={
                      <>
                        {item.content}
                        <br />
                        <Typography.Text type="secondary">
                          {item.sortTime}
                        </Typography.Text>
                      </>
                    }
                  />
                </List.Item>
              )}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="暂无其他消息"
            />
          )}
        </div>
      }
    >
      <Button type="text" aria-label="消息" style={{ width: 40, height: 40 }}>
        <Badge count={unread} size="small">
          <BellOutlined style={{ fontSize: 22 }} />
        </Badge>
      </Button>
    </Popover>
  );
};

export default ProjectAccountNotifications;
